import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAnalyses, type AnalysisSummary, type AnalysisStatus, type Verdict } from '../api.js';
import { AppHeader } from '../components/AppHeader.js';
import { BackLink } from '../components/BackLink.js';
import { dealTitle } from '../components/DealIdentity.js';
import { timestamp } from '../format.js';
import { useSession } from '../useSession.js';

/**
 * The tenant's deal history — the way back to an underwrite you already ran.
 *
 * Until this page existed, a finished report was reachable only by the id the app handed you at
 * submission: navigate away, close the tab, or come back tomorrow, and the underwrite was gone as far
 * as the UI was concerned. `ProjectView` names a project but never linked to its runs, and nothing
 * else listed them. A report a client cannot be sent back to is a report nobody keeps.
 */

type Load = { status: 'loading' } | { status: 'ready'; runs: AnalysisSummary[] };

const VERDICT_LABEL: Record<Verdict, string> = {
  READY: 'Ready',
  ADVANCE: 'Advance',
  WORKSHOP: 'Workshop',
  RESUBMIT: 'Resubmit',
};

/**
 * What a row says when the run did not finish. A history that showed only completed runs would hide
 * exactly the ones a person is looking for — the one that failed, or the one still going.
 */
const STATUS_LABEL: Partial<Record<AnalysisStatus, string>> = {
  queued: 'Queued',
  running: 'Underwriting',
  awaiting_index: 'Reading documents',
  failed: 'Did not finish',
  canceled: 'Stopped',
};

/**
 * How a run is named in the list.
 *
 * Deliberately `dealTitle` — the SAME function the report's header uses. A list that named a deal one
 * way and its own report another would make the two look like different deals, which is the precise
 * confusion this page exists to end. "Untitled deal" is honest rather than blank: 53 of the runs on
 * staging today carry no project, address or sponsor, and a blank row reads as a rendering fault.
 */
export function runLabel(run: AnalysisSummary): string {
  return dealTitle(run).title ?? 'Untitled deal';
}

/**
 * Does this row carry a "Superseded" mark? (R8 currency on the list — audit item X1.)
 *
 * 🔴 THREE WIRE STATES, AND ONLY ONE SPEAKS.
 *   • `true`  — a newer succeeded underwrite of the same project exists. Mark it.
 *   • `false` — a real "this is the latest". Renders NOTHING: the positive claim belongs on the
 *     report, where the reader is committing to the numbers, not on a list whose job is scanning.
 *   • absent  — the question does not apply (an unbound run belongs to no chain; an unfinished run is
 *     not a version of anything yet). Also nothing, and for a different reason.
 *
 * ⚠️ `=== true`, NOT truthiness — and this is a named function rather than an inline `&&` precisely so
 * the distinction is testable. A truthiness check behaves identically until a `false` arrives, which
 * is the exact moment the difference between "checked, current" and "never checked" starts to matter.
 */
export function showsSuperseded(run: Pick<AnalysisSummary, 'superseded'>): boolean {
  return run.superseded === true;
}

/**
 * What a row says about the DOCUMENTS having moved since it was underwritten — the other half of R8
 * currency, and the half a list row has never carried (#770 shipped supersession alone).
 *
 * The two answer different failures and only one can apply at a time. Supersession says *a newer
 * report exists — open that one instead.* This says *this IS the newest report, and the package it
 * was produced from has moved underneath it.* The remedy differs (open vs. re-run), so the sentences
 * must differ too.
 *
 * 🔴 THREE STATES, ONE SPEAKS — the same discipline as `showsSuperseded` and for the same reason.
 *   • `> 0`      — documents landed after this underwrite began. Say so.
 *   • `0`        — asked, and nothing moved. SILENT here: the positive claim of currency belongs on
 *                  the report and the project page, where a reader is committing to the numbers, not
 *                  on a list whose job is scanning.
 *   • `undefined`— never asked (superseded, unfinished, or bound to no project record). Also silent,
 *                  and for a different reason.
 *
 * ⚠️ `n > 0` ON A `number | undefined`, never `n ?? 0 > 0` and never truthiness on the count. The
 * three states collapse into two the moment an absent count is coerced, and the collapse is
 * invisible until the day a real `0` arrives — which is exactly when "we checked and nothing moved"
 * and "we never checked" stop meaning the same thing.
 */
export function documentsSinceLabel(
  n: number | undefined,
  revised?: number | undefined,
): string | null {
  /*
    Added and REVISED are two movements and two words: a newer version of a document already on the
    record is not a document added, and a re-saved file is not two. `revised` follows the same three
    states — only a positive number speaks.
  */
  const parts: string[] = [];
  if (n !== undefined && n > 0) parts.push(`${n} document${n === 1 ? '' : 's'} added`);
  if (revised !== undefined && revised > 0) {
    parts.push(`${revised} document${revised === 1 ? '' : 's'} revised`);
  }
  return parts.length === 0 ? null : `⚠ ${parts.join(' and ')} since`;
}

/**
 * What a row says about whether the CLIENT has read this report.
 *
 * ⚠️ THREE STATES AND THEY ARE DIFFERENT SENTENCES. `undefined` is "you are not shown this" (the
 * caller is a client, and the server omits the field) — the row stays silent. `0` is the finding the
 * whole feature exists for: **we produced this and nobody has opened it.** Anything else is
 * engagement, dated.
 *
 * 🔴 A `count ?? 0` HERE WOULD DESTROY THE DISTINCTION and print "not opened" on every client's own
 * screen — the exact absent-versus-zero collapse `CurrencyBadge` and the R1 comparison both refuse.
 */
export function engagementLabel(
  clientViews: number | undefined,
  lastClientViewAt: string | null | undefined,
): string | null {
  if (clientViews === undefined) return null;
  if (clientViews === 0) return 'Not opened by the client';
  const times = clientViews === 1 ? 'once' : `${clientViews} times`;
  return lastClientViewAt
    ? `Client opened ${times} · last ${timestamp(lastClientViewAt)}`
    : `Client opened ${times}`;
}

export function AnalysesList() {
  const navigate = useNavigate();
  const session = useSession();
  const [params] = useSearchParams();
  const project = params.get('project');
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    if (session.status !== 'authed') return;
    let cancelled = false;
    setLoad({ status: 'loading' });
    void getAnalyses(project ? { project } : {}).then((runs) => {
      if (!cancelled) setLoad({ status: 'ready', runs });
    });
    return () => {
      cancelled = true;
    };
  }, [session.status, project]);

  if (session.status !== 'authed') {
    return <div className="app__loading">Loading…</div>;
  }

  return (
    <div className="app">
      <AppHeader claims={session.claims} />
      <main className="app__main">
        {/* Filtered to a project? Then this page was reached FROM that project, and the way back to
            it is the affordance a reader wants — not a generic "all deals". */}
        {project && (
          <div className="app__crumbs">
            <BackLink
              fallback={`/projects/${encodeURIComponent(project)}`}
              label="Back to project"
            />
            <button type="button" className="link-btn" onClick={() => void navigate('/analyses')}>
              All deals
            </button>
          </div>
        )}

        <div className="app__intro">
          <h1>Deals</h1>
          <p className="sub">
            {project ? (
              <>
                Every deal underwritten under project <strong>{project}</strong>, newest first.
              </>
            ) : (
              <>
                Every deal you have underwritten, newest first. Open one to read its report, or
                start a new analysis from the home page.
              </>
            )}
          </p>
        </div>

        {load.status === 'loading' && <div className="app__loading">Loading your deals…</div>}

        {load.status === 'ready' &&
          (load.runs.length === 0 ? (
            <div className="notice">
              {project ? (
                <>
                  <strong>No deals under {project} yet.</strong> Upload a document package and
                  assign it to this project, and its runs will appear here.
                </>
              ) : (
                <>
                  <strong>No deals yet.</strong> Submit a deal or upload a document package, and it
                  will appear here.
                </>
              )}
            </div>
          ) : (
            <ul className="runlist">
              {load.runs.map((r) => {
                const done = r.status === 'succeeded';
                const rec = r.recommendation;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="runlist__row"
                      onClick={() => void navigate(`/analyses/${r.id}`)}
                    >
                      <span className="runlist__main">
                        <span className="runlist__name">{runLabel(r)}</span>
                        <span className="runlist__meta">
                          {done
                            ? `Completed ${timestamp(r.completedAt)}`
                            : (STATUS_LABEL[r.status] ?? r.status)}
                          {r.project?.code ? ` · ${r.project.code}` : ''}
                        </span>
                        {/*
                          R8 currency on the LIST (X1). Marks only the SUPERSEDED rows.

                          ⚠️ THE POSITIVE CLAIM IS DELIBERATELY NOT MADE HERE. A "current" chip on
                          every other row would be noise on the one screen whose job is to let a
                          reader scan — and the report itself states its currency positively the
                          moment they open it (#766). So the list's job is narrower and honest: stop
                          someone opening a stale report believing it is the position today.

                          ⚠️ `=== true`, not truthiness. Absent means the question does not apply
                          (unbound, or unfinished) and must render nothing; `false` is a real "this
                          is the latest" and also renders nothing here. Only `true` speaks.
                        */}
                        {showsSuperseded(r) && (
                          <span className="runlist__superseded">Superseded</span>
                        )}
                        {/*
                          The documents-moved half of R8 currency on the list.

                          ⚠️ MUTUALLY EXCLUSIVE WITH THE CHIP ABOVE BY CONSTRUCTION, not by a
                          condition here: the server sends `documentsAddedSince` only on the row it
                          also marks `superseded: false`. A row can therefore never say both "open
                          the newer one" and "re-run this one", which would be two remedies for one
                          deal and no way to choose.
                        */}
                        {(() => {
                          const label = documentsSinceLabel(
                            r.documentsAddedSince,
                            r.documentsRevisedSince,
                          );
                          return label ? <span className="runlist__stale">{label}</span> : null;
                        })()}
                        {/* Operator-only, and only on a finished report — an unfinished run has
                            nothing to have been opened. See `engagementLabel` for why an absent
                            count and a zero say different things. */}
                        {done &&
                          (() => {
                            const label = engagementLabel(r.clientViews, r.lastClientViewAt);
                            return label ? (
                              <span
                                className={`runlist__engagement${r.clientViews === 0 ? ' runlist__engagement--none' : ''}`}
                              >
                                {label}
                              </span>
                            ) : null;
                          })()}
                      </span>
                      {/* The conclusion, at a glance. Absent on a run that never reached one — an
                          empty slot rather than a "—" pretending to be a score. */}
                      {done && rec && (
                        <span className={`runlist__verdict runlist__verdict--${rec.toLowerCase()}`}>
                          {VERDICT_LABEL[rec] ?? rec}
                          {r.score != null && <b className="runlist__score">{r.score}</b>}
                        </span>
                      )}
                      <span className="runlist__go" aria-hidden="true">
                        →
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ))}
      </main>
    </div>
  );
}
